// TODO(bpm): Investigate a monospace font with support for dot product unicode character.
// https://developer.chrome.com/docs/devtools/console/api/
// https://developer.mozilla.org/en-US/docs/Web/API/console#examples
// https://stackoverflow.com/questions/7505623/colors-in-javascript-console#comment18159348_7505879
// https://www.uxmatters.com/mt/archives/2007/01/applying-color-theory-to-digital-displays.php
// https://medium.com/@pvermaer/down-the-font-legibility-rabbit-hole-481f207a6013
// https://htmlcolorcodes.com/colors/

// DNS: Remove duplication with color.ts and random.ts
export type ColorRgb = {
  r: number;
  g: number;
  b: number;
};

function byteToHex(byte: number): string {
  return `0${byte.toString(16)}`.slice(-2);
}

function colorAsHex(color: ColorRgb) {
  return `#${byteToHex(color.r)}${byteToHex(color.g)}${byteToHex(color.b)}`;
}

export type LogStyle = Readonly<{
  // Colors
  fg?: ColorRgb | undefined;
  bg?: ColorRgb | undefined;
  reverse?: boolean | undefined;
  // Font styles
  bold?: boolean | undefined;
  italic?: boolean | undefined;
  underline?: boolean | undefined;
  // Verbatim css
  css?: string[] | undefined;
}>;

function merge(...styles: LogStyle[]): LogStyle {
  const result = styles.reduce((p, c) => {
    return {
      fg: c.fg ?? p.fg,
      bg: c.bg ?? p.bg,
      reverse: c.reverse ?? p.reverse,
      bold: c.bold ?? p.bold,
      italic: c.italic ?? p.italic,
      underline: c.underline ?? p.underline,
      css: [...(p.css ?? []), ...(c.css ?? [])],
    };
  }, {});
  return Object.freeze(result);
}

export type StyleData = { css: string; ansi: string; style: LogStyle };

function buildCss(style: LogStyle) {
  const reverse = style.reverse ?? false;
  const fg = reverse ? style.bg : style.fg;
  const bg = reverse ? style.fg : style.bg;
  const css = [];
  if (fg !== undefined) {
    css.push(`color: ${colorAsHex(fg)}`);
  }
  if (bg !== undefined) {
    css.push(`background-color: ${colorAsHex(bg)}`);
  }
  if (style.bold !== undefined) {
    css.push(`font-weight: ${style.bold ? 'bold' : 'normal'}`);
  }
  if (style.italic !== undefined) {
    css.push(`font-style: ${style.italic ? 'italic' : 'normal'}`);
  }
  if (style.underline !== undefined) {
    css.push(`text-decoration: ${style.underline ? 'underline' : 'none'}`);
  }
  if (style.css !== undefined) {
    css.push(...style.css);
  }
  return css.join('; ');
}

// https://stackoverflow.com/a/45074641/8107589
const isVsCodeDebug =
  typeof process !== 'undefined' && /--debug|--inspect/.test(process.execArgv.join(' '));

// Different environments support different ansi color modes.
//  git bash: 24bit, 8bit, 4bit, 4bit-bold, 3bit
//  vscode debug console: 4bit, 3bit (4bit-bold does not have a satisfactory bright black)
export type AnsiColorType = '24bit' | '8bit' | '4bit' | '4bit-bold' | '3bit';
export const AnsiColorMode: AnsiColorType = isVsCodeDebug ? '4bit' : '24bit';

function rgbToAnsi24bit({ r, g, b }: ColorRgb): [number, string] {
  return [8, `;2;${r};${g};${b}`];
}

function rgbToAnsi8bit({ r, g, b }: ColorRgb): [number, string] {
  let code: number;
  if (r === g && r === b) {
    // There are 26 shades of gray: full black, full white, and 24 intermediate shades.
    const grayscale = Math.round((r / 255) * 25);
    if (grayscale === 0) {
      code = 16;
    } else if (grayscale === 25) {
      code = 231;
    } else {
      code = 232 + grayscale - 1;
    }
  } else {
    // Otherwise, we have a full color cube of 6 shades of red, green, and blue;
    code =
      16 +
      36 * Math.round((r / 255) * 5) +
      6 * Math.round((g / 255) * 5) +
      Math.round((b / 255) * 5);
  }
  return [8, `;5;${code}`];
}

export interface ColorHsv {
  h: number;
  s: number;
  v: number;
}

function rgbToHsv(color: ColorRgb): ColorHsv {
  const { r, g, b } = color;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  const s = max === 0 ? 0 : d / max;
  const v = max / 255;
  let h;
  switch (max) {
    case min:
      h = 0;
      break;
    case r:
      h = g - b + d * (g < b ? 6 : 0);
      h /= 6 * d;
      break;
    case g:
      h = b - r + d * 2;
      h /= 6 * d;
      break;
    case b:
      h = r - g + d * 4;
      h /= 6 * d;
      break;
    default:
      throw new Error(`max(${max}) != r(${r})|g(${g})|b(${b})`);
  }
  return { h, s, v };
}

// Mapping from hue angle to ANSI color.
const hueToColor = [
  1, //   0° - Red
  3, //  60° - Yellow
  2, // 120° - Green
  6, // 180° - Cyan
  4, // 240° - Blue
  5, // 300° - Purple
];

// At s = 1, we treat v < .25 as black.
const gray1 = [1, 0.25];
// At v = 1, we treat s < .125 as white.
const gray2 = [0.125, 1];
// Other grayscale values are linearly interpolated between those points.
const graySlope = (gray2[1] - gray1[1]) / (gray2[0] - gray1[0]);
const grayIntercept = gray2[1] - graySlope * gray2[0];

function rgbToAnsi4bit({ r, g, b }: ColorRgb, bg: boolean): [number, string] {
  // Background colors do not support intensity.
  const mode = bg ? '3-bit' : AnsiColorMode;

  // Convert to HSV so we can use hue to find color and value to find intensity.
  const { h, s, v } = rgbToHsv({ r, g, b });

  // gray: y < mx + b
  const gray = v < graySlope * s + grayIntercept;

  let code = 0;
  let high = false;
  if (gray) {
    // Black: 0%, Bright Black: 50%, White: 75%, Bright White: 100%
    code = v >= 0.625 ? 7 : 0;
    high = (v >= 0.3125 && v < 0.625) || v >= 0.875;
  } else {
    // Divide the hue into 6 equal segments with Red @ 0, Cyan @ 0.5
    code = hueToColor[Math.round(h * 6)];
    high = v > 0.75;
  }
  let suffix = '';
  if (high) {
    code += 60;
    suffix = ';1';
  }

  const debug = false as boolean;
  if (!bg && debug) {
    console.log('rgbToAnsi4bit', { r, g, b, h, s, v, gray, code, high, graySlope, grayIntercept });
  }

  // Some implementations don't support full 4-bit, so gracefully degrade.
  if (mode !== '4bit') {
    if (code >= 60) {
      code -= 60;
      if (mode !== '4bit-bold') {
        suffix = '';
        if (mode === '3bit' && code === 0) {
          // ANSI code often assumes that all colors, including dark gray, are legible on black.
          // In 3-bit color, dark gray becomes black so we make it white, instead.
          code = 7;
        }
      }
    }
  }
  return [code, suffix];
}

function ansiColor(color: ColorRgb, bg: boolean): [number, string] {
  switch (AnsiColorMode) {
    case '24bit':
      return rgbToAnsi24bit(color);
    case '8bit':
      return rgbToAnsi8bit(color);
    default:
      return rgbToAnsi4bit(color, bg);
  }
}

function buildAnsi(style: LogStyle) {
  // https://stackoverflow.com/questions/4842424/list-of-ansi-color-escape-sequences
  // https://en.wikipedia.org/wiki/ANSI_escape_code
  const code = ['\x1b[0'];
  if (style.bold === true) {
    code.push(';1');
  }
  if (style.italic === true) {
    code.push(';3');
  }
  if (style.underline === true) {
    code.push(';4');
  }
  if (style.reverse === true) {
    code.push(';7');
  }
  if (style.fg) {
    const [c, suffix] = ansiColor(style.fg, false);
    code.push(`;${30 + c}${suffix}`);
  }
  if (style.bg) {
    const [c, suffix] = ansiColor(style.bg, true);
    // code.push('m\x1b[');
    code.push(`;${40 + c}${suffix}`);
  }
  code.push('m');
  // console.log('buildAnsi', JSON.stringify(code.join('')), style);
  return code.join('');
}

export function buildStyle(base: LogStyle, ...overrides: LogStyle[]): StyleData {
  const style = overrides.length ? merge(base, ...overrides) : base;
  return {
    css: buildCss(style),
    ansi: buildAnsi(style),
    style,
  };
}

// TODO: Switch to ANSI colors which adapt to light and dark theme automatically.
const defaultToFixedWidth = false as boolean;
const fontSize = '12px'; // '11px';
const smallFontSize = '11px';
const fixedWidthFamily = 'Consolas';
const variableWidthFamily = 'Calibri';

const stylesFont: LogStyle = {
  bold: false,
  css: [
    `font-size: ${fontSize}`,
    // TODO(bpm): Install access to other monospaced fonts?
    `font-family: ${defaultToFixedWidth ? fixedWidthFamily : variableWidthFamily}`,
  ],
} as const;

const stylesHeader = merge(stylesFont, {
  fg: { r: 0xdd, g: 0xdd, b: 0xdd },
  bg: { r: 0x00, g: 0x33, b: 0x66 },
  css: ['display: inline-block'],
  // fg: 'white',
  // fg: '#297',
  // fg: '#dcdcaa',
});

export const Style = {
  default: buildStyle(stylesFont), // Built-in log color is #EEE

  warn: buildStyle(stylesFont, {
    fg: { r: 0xfd, g: 0xf3, b: 0xaa },
    bg: { r: 0x41, g: 0x3c, b: 0x26 },
  }),
  error: buildStyle(stylesFont, {
    fg: { r: 0xf9, g: 0xde, b: 0xdc },
    bg: { r: 0x4e, g: 0x35, b: 0x34 },
    bold: true,
  }),

  inlineHeader: buildStyle(stylesHeader, {
    bg: { r: 0, g: 0, b: 0 },
    css: ['padding: 1px 5px 1px 5px'],
  }),
  groupHeader: buildStyle(stylesHeader, {
    bg: { r: 0, g: 0, b: 0 },
    // These don't work to expand to the full width, so we pad 300px to get some visual separation.
    // 'width: 100%',
    // 'display: flex; flex-grow: 1',
    css: ['padding: 1px 300px 1px 5px'],
  }),
  skipCount: buildStyle({
    fg: { r: 0, g: 0, b: 0 },
    bg: { r: 0x5d, g: 0xb0, b: 0xd7 },
    css: [
      `font-size: ${smallFontSize}`,
      `font-family: ${variableWidthFamily}`,
      'border-radius: 8px',
      'padding: 0px 3px 0px 3px',
      'text-decoration: line-through',
    ],
  }),

  keyword: buildStyle(stylesFont, { fg: { r: 0x56, g: 0x9c, b: 0xd6 } }),
  key: buildStyle(stylesFont, { fg: { r: 0x9c, g: 0xdc, b: 0xfe } }), // fg: '#F0E68C'),
  method: buildStyle(stylesFont, { fg: { r: 0x9c, g: 0xdc, b: 0xfe }, italic: true }),
  // N.B. You can't style "%o".
  // TODO(bpm): Consider replacing %o with custom formatting. However, %o has a lot of built in value
  // so it may not be an upgrade.
  value: buildStyle(stylesFont),
  subtle: buildStyle(stylesFont, { fg: { r: 0x77, g: 0x77, b: 0x77 } }),
  number: buildStyle(stylesFont, { fg: { r: 0xb5, g: 0xce, b: 0xa8 } }), // fg: '#6495ED'),
  string: buildStyle(stylesFont, { fg: { r: 0xce, g: 0x91, b: 0x78 } }), // fg: '#35d4c7'),
  type: buildStyle(stylesFont, { fg: { r: 0x4e, g: 0xc9, b: 0xb0 } }), // fg: '#35d4c7'),
  comment: buildStyle(stylesFont, { fg: { r: 0x22, g: 0x99, b: 0x77 } }),
  highlight: buildStyle(stylesFont, { fg: { r: 0x9c, g: 0xdc, b: 0xfe } }),
  reference: buildStyle(stylesFont, { fg: { r: 0xdc, g: 0xdc, b: 0xaa }, italic: true }),
  // Logs originating from console.log or console.info.
  // Note: VSCode debug console coerces to ANSI colors.
  console: buildStyle(stylesFont, { fg: { r: 0x22, g: 0x99, b: 0x5d } }),
  // console: buildStyle(stylesFont, { fg: { r: 0x22, g: 0x99, b: 0x77 } }),
  // console: styles(stylesFont, { fg: '#AAA', italic: true }),
  callSite: buildStyle(stylesFont, {
    // bg: '#555',
    // fg: '#000',
    fg: { r: 0x55, g: 0x55, b: 0x55 },
    css: [
      'display: inline-block',
      // 'padding: 1px 3px 1px 3px'
      'padding-left: 0.5em',
      'margin-left: 0.5em',
      'border-left: 1px solid #333',
    ],
  }),
  // Nice colors:
  // #FFBF00
  dateTime: buildStyle(stylesFont, {
    fg: { r: 0xff, g: 0xbf, b: 0x00 },
  }),
  // #00FFFF
  nice2: buildStyle(stylesFont, {
    fg: { r: 0x00, g: 0xff, b: 0xff },
  }),
  // #F0E68C
  nice3: buildStyle(stylesFont, {
    fg: { r: 0xf0, g: 0xe6, b: 0x8c },
  }),
} as const satisfies Record<string, StyleData>;
